import { useCallback, useEffect, useState } from "react";
import { Brain, Plus, X, Loader2, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { fmtFt } from "@/lib/walls";

const Pref = ({ p, onDelete }) => (
  <li className="group flex items-start gap-3 py-2.5 border-b border-[var(--line)]/60" data-testid={`pref-${p.id}`}>
    {p.source === "you" ? <User size={14} className="mt-0.5 text-[var(--amber)] shrink-0" /> : <Sparkles size={14} className="mt-0.5 text-[var(--cyan)] shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-sm leading-snug">{p.text}</p>
      {p.from && <p className="mt-0.5 text-[11px] text-[var(--muted)] truncate">Learned from: “{p.from}”</p>}
    </div>
    <button data-testid={`delete-pref-${p.id}`} onClick={onDelete} title="Forget this"
      className="text-[var(--muted)] hover:text-red-400 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"><X size={14} /></button>
  </li>
);

export const DesignMemory = () => {
  const [mem, setMem] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/memory").then(({ data }) => setMem(data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post("/memory/prefs", { text }); setText(""); await load(); toast.success("Preference saved"); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  const del = async (id) => {
    try { await api.delete(`/memory/prefs/${id}`); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (enabled) => {
    try {
      await api.put("/memory", { enabled });
      setMem((m) => ({ ...m, enabled }));
      toast.success(enabled ? "Learning on" : "Learning paused — drafts ignore your memory");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!mem) return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[var(--cyan)]" /></div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8" data-testid="design-memory">
      <div className="min-w-0 border border-[var(--line)] bg-[var(--surface)] p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5"><Brain size={18} className="text-[var(--cyan)]" /><h3 className="font-display font-semibold">What the AI knows about your taste</h3></div>
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            Learning <Switch data-testid="memory-enabled-switch" checked={mem.enabled} onCheckedChange={toggle} />
          </label>
        </div>
        <p className="mt-2 text-xs text-[var(--muted)]">Every brief, revision and wall you drag teaches it. These are applied to every new draft and revision.</p>
        <form onSubmit={add} className="mt-5 flex gap-2">
          <input data-testid="add-pref-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Always put the primary suite at the back, away from the street"
            className="flex-1 min-w-0 bg-[var(--card)] border border-[var(--line)] focus:border-[var(--cyan)] outline-none px-3 py-2.5 text-sm transition-colors" />
          <button data-testid="add-pref-btn" disabled={busy || !text.trim()} className="px-4 bg-[var(--cyan)] text-[var(--bg)] disabled:opacity-40 hover:brightness-110 transition-[filter,opacity]">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          </button>
        </form>
        <ul className="mt-4" data-testid="pref-list">
          {mem.prefs.length === 0 && <li className="text-sm text-[var(--muted)] py-3" data-testid="prefs-empty">Nothing learned yet — draft and revise a few plans, or add your own rules above.</li>}
          {[...mem.prefs].reverse().map((p) => <Pref key={p.id} p={p} onDelete={() => del(p.id)} />)}
        </ul>
      </div>
      <div className="min-w-0 border border-[var(--line)] p-5 sm:p-6" data-testid="memory-habits">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--amber)]">Measured from your drafts</p>
        <h3 className="mt-2 font-display font-semibold">Your typical room sizes</h3>
        {mem.habits.length === 0 ? <p className="mt-3 text-sm text-[var(--muted)]">Appears once you have a couple of drafts.</p> : (
          <div className="mt-4 space-y-2">
            {mem.habits.map((h) => (
              <div key={h.type} className="flex items-center justify-between text-sm border-b border-[var(--line)]/60 pb-2" data-testid={`habit-${h.type}`}>
                <span className="capitalize">{h.type}</span>
                <span className="font-mono text-xs text-[var(--muted)]">{fmtFt(h.w)} × {fmtFt(h.h)} <span className="text-[var(--cyan)]">· {h.count} rooms</span></span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-[11px] text-[var(--muted)] leading-relaxed">These sizes are fed to the AI as your defaults, so new drafts start out the way you usually lay things out.</p>
      </div>
    </div>
  );
};
